// aldoo-ra/Router/reader.js

// Use Vite's glob import for routes
const defaultRoutes = import.meta.glob(["/aldoo-ra/**/routes.js", "!/aldoo-ra/aldoo-mcp/**"], { eager: true })
const userRoutes = import.meta.glob("/components/**/routes.js", { eager: true })

// Build the set of route prefixes that opt out of language handling at module-load time,
// so the boot-time detection block below can use it before the class is ready.
const _noLangPrefixes = []
for (const module of [...Object.values(defaultRoutes), ...Object.values(userRoutes)]) {
  if (module.routeOptions && typeof module.routeOptions === 'object') {
    for (const [pattern, opts] of Object.entries(module.routeOptions)) {
      if (opts.noLanguagePrefix) {
        _noLangPrefixes.push(pattern.endsWith('*') ? pattern.slice(0, -1) : pattern)
      }
    }
  }
}
const _isNoLangUrl = (cleanPath) => _noLangPrefixes.some((p) => cleanPath.startsWith(p))

// IMMEDIATE LANGUAGE DETECTION FROM URL
// This runs as soon as the module is imported, before any React components mount
if (typeof window !== 'undefined') {
  const currentPath = window.location.pathname
  const langMatch = currentPath.match(/^\/([a-z]{2})(\/|$)/)

  if (langMatch) {
    const langCode = langMatch[1]
    // Strip the lang prefix to get the clean path
    const cleanPath = currentPath.slice(langCode.length + 1) || '/'

    // Skip language detection for admin and noLanguagePrefix routes
    if (!cleanPath.startsWith('/admin') && !_isNoLangUrl(cleanPath)) {
      // Save previous language so we can restore it if the code turns out to be invalid
      window.__prevLangBeforeUrlSwitch = localStorage.getItem('current-site-language')

      // Set localStorage immediately (will be reverted if invalid)
      const storedLang = localStorage.getItem('current-site-language')

      if (storedLang !== JSON.stringify(langCode)) {
        localStorage.setItem('current-site-language', JSON.stringify(langCode))
      }

      // Set cookie immediately (will be reverted if invalid)
      document.cookie = `preferred-language=${langCode}; path=/; max-age=31536000; SameSite=Lax`
    }
  }
}

export class RouteReader {
  static #routeCache = new Map()
  // Collected routeOptions entries from all routes.js files: Map<routePattern, options>
  static #routeOptionsCache = new Map()
  static initialized = false
  static #initializing = false

  static init() {
    if (this.initialized || this.#initializing) return

    this.#initializing = true

    // Auto-discover onBeforeRender hooks on server-side only (async, not awaited)
    if (typeof window === 'undefined') {
      this.#autoDiscoverHooks()
    }

    //Read the default
    Object.entries(defaultRoutes).forEach(([, module]) => {
      if (module.default && typeof module.default === 'object') {
        Object.entries(module.default).forEach(([routePath, component]) => {
          const normalizedPath = routePath.endsWith("*") ? routePath : `${routePath}`
          this.#routeCache.set(normalizedPath, component)
        })
      }
      this.#collectRouteOptions(module)
    })
    //Read the user routes
    Object.entries(userRoutes).forEach(([, module]) => {
      if (module.default && typeof module.default === 'object') {
        Object.entries(module.default).forEach(([routePath, component]) => {
          const normalizedPath = routePath.endsWith("*") ? routePath : `${routePath}`
          this.#routeCache.set(normalizedPath, component)
        })
      }
      this.#collectRouteOptions(module)
    })

    this.#initializing = false
    this.initialized = true
  }

  // New method that properly awaits hook discovery
  static async initWithHookDiscovery() {
    if (this.initialized || this.#initializing) return

    this.#initializing = true

    // Clear any existing state to ensure clean initialization
    this.#routeCache.clear()
    const { HookRegistry } = await import('../AldooVike/hooks/HookRegistry.js')
    HookRegistry.clearHooks()

    // Auto-discover onBeforeRender hooks on server-side only (properly awaited)
    if (typeof window === 'undefined') {
      await this.#autoDiscoverHooks()
    }

    //Read the default
    Object.entries(defaultRoutes).forEach(([, module]) => {
      if (module.default && typeof module.default === 'object') {
        Object.entries(module.default).forEach(([routePath, component]) => {
          const normalizedPath = routePath.endsWith("*") ? routePath : `${routePath}`
          this.#routeCache.set(normalizedPath, component)
        })
      }
      this.#collectRouteOptions(module)
    })
    //Read the user routes
    Object.entries(userRoutes).forEach(([, module]) => {
      if (module.default && typeof module.default === 'object') {
        Object.entries(module.default).forEach(([routePath, component]) => {
          const normalizedPath = routePath.endsWith("*") ? routePath : `${routePath}`
          this.#routeCache.set(normalizedPath, component)
        })
      }
      this.#collectRouteOptions(module)
    })

    this.#initializing = false
    this.initialized = true
  }

  // Auto-discover onBeforeRender hooks from components in routes
  static async #autoDiscoverHooks() {
    try {
      const { HookRegistry } = await import('../AldooVike/hooks/HookRegistry.js')

      // Process user routes to find components with attached onBeforeRender hooks
      Object.entries(userRoutes).forEach(([, module]) => {
        if (module.default && typeof module.default === 'object') {
          // Check each component for attached onBeforeRender hooks
          Object.entries(module.default).forEach(([routePath, component]) => {
            if (component && component.onBeforeRender && typeof component.onBeforeRender === 'function') {
              const normalizedPath = routePath.endsWith("*") ? routePath : `${routePath}`
              HookRegistry.registerOnBeforeRenderHook(normalizedPath, component.onBeforeRender)
            }
          })
        }
      })
    } catch (error) {
      console.warn('Could not auto-discover onBeforeRender hooks:', error)
    }
  }

  // Collect routeOptions exported from a routes module
  static #collectRouteOptions(module) {
    if (module.routeOptions && typeof module.routeOptions === 'object') {
      Object.entries(module.routeOptions).forEach(([routePath, opts]) => {
        this.#routeOptionsCache.set(routePath, opts)
      })
    }
  }

  // Returns true if the given clean URL (no lang prefix) matches a route with noLanguagePrefix: true
  static #hasNoLanguagePrefix(cleanUrl) {
    for (const [pattern, opts] of this.#routeOptionsCache.entries()) {
      if (!opts.noLanguagePrefix) continue
      if (pattern.endsWith('*')) {
        if (cleanUrl.startsWith(pattern.slice(0, -1))) return true
      } else if (cleanUrl === pattern) {
        return true
      }
    }
    return false
  }

  static registerRoutes(pathOrPaths, component) {
    if (!this.initialized && !this.#initializing) {
      this.init()
    }

    if (typeof pathOrPaths === "object" && !component) {
      Object.entries(pathOrPaths).forEach(([path, comp]) => {
        // If comp is a string, treat it as a redirect target
        if (typeof comp === 'string') {
          const normalizedPath = path.endsWith("*") ? path : `${path}`
          this.#routeCache.set(normalizedPath, comp)
        } else {
          const normalizedPath = path.endsWith("*") ? path : `${path}`
          this.#routeCache.set(normalizedPath, comp)
        }
      })
    } else {
      const normalizedPath = pathOrPaths.endsWith("*")
        ? pathOrPaths
        : `${pathOrPaths}`
      this.#routeCache.set(normalizedPath, component)
    }
  }

  static getRoutes() {
    if (!this.initialized && !this.#initializing) {
      this.init()
    }
    return this.#routeCache
  }

  static #stripLanguageCode(pathname) {
    // Extract language code from pathname (e.g., /fr/about -> /about)
    const langMatch = pathname.match(/^\/([a-z]{2})(\/|$)/)
    if (langMatch) {
      const langCode = langMatch[1]
      const urlWithoutLang = pathname.slice(langCode.length + 1) || '/'
      return { langCode, urlWithoutLang }
    }
    return { langCode: null, urlWithoutLang: pathname }
  }

  static prependLanguageCode(url, langCode) {
    // Prepend language code to URL (e.g., /about + fr -> /fr/about)
    if (!langCode) return url

    // Handle root URL
    if (url === '/') return `/${langCode}`

    // Ensure URL starts with /
    const cleanUrl = url.startsWith('/') ? url : `/${url}`

    return `/${langCode}${cleanUrl}`
  }

  static resolve(pathname) {
    if (!this.initialized && !this.#initializing) {
      this.init()
    }

    // Strip language code if present
    const { langCode, urlWithoutLang } = this.#stripLanguageCode(pathname)

    // If language code detected, trigger language change (skip for admin and noLanguagePrefix routes)
    if (langCode && typeof window !== 'undefined' && !urlWithoutLang.startsWith('/admin') && !this.#hasNoLanguagePrefix(urlWithoutLang)) {
      this.#setLanguageFromUrl(langCode)
    }

    // First try exact match with clean URL
    if (this.#routeCache.has(urlWithoutLang)) {
      const result = this.#routeCache.get(urlWithoutLang)

      // If result is a string, return it as a redirect target (don't resolve recursively)
      if (typeof result === 'string') {
        return result
      }

      // After successful resolution, prepend language code to URL if not present
      this.#prependLanguageToUrl(pathname, langCode)
      return result
    }

    // Then try wildcard matches with clean URL
    for (const [route, component] of this.#routeCache.entries()) {
      if (route.endsWith("*")) {
        const baseRoute = route.slice(0, -1)
        // Only match if pathname starts with baseRoute and has additional segments
        // Don't match exact base route (e.g., /blog/* should not match /blog)
        if (urlWithoutLang.startsWith(baseRoute) && urlWithoutLang !== baseRoute.slice(0, -1)) {
          // If component is a string, return it as a redirect target (don't resolve recursively)
          if (typeof component === 'string') {
            return component
          }

          // After successful resolution, prepend language code to URL if not present
          this.#prependLanguageToUrl(pathname, langCode)
          return component
        }
      }
    }
    return null
  }

  static #prependLanguageToUrl(currentPathname, existingLangCode) {
    // Only run on client-side
    if (typeof window === 'undefined') return

    // Strip language code to check the clean path
    const { urlWithoutLang } = this.#stripLanguageCode(currentPathname)

    // Skip for admin and noLanguagePrefix routes — these manage their own UI/language
    if (urlWithoutLang.startsWith('/admin') || this.#hasNoLanguagePrefix(urlWithoutLang)) {
      if (existingLangCode) {
        window.history.replaceState(window.history.state || {}, '', urlWithoutLang)
      }
      return
    }

    // If URL already has language code, do nothing
    if (existingLangCode) {
      return
    }

    // Get current language from localStorage
    const storedLang = localStorage.getItem('current-site-language')
    if (!storedLang) {
      return
    }

    let langCode
    try {
      langCode = JSON.parse(storedLang)
    } catch (e) {
      langCode = storedLang
    }

    // Don't prepend if language is default (en)
    if (langCode === 'en') {
      return
    }

    // Build new URL with language code
    const newUrl = `/${langCode}${currentPathname}`

    // Replace URL without reloading the page (preserve query string)
    window.history.replaceState(window.history.state || {}, '', newUrl + window.location.search)
  }

  static #setLanguageFromUrl(langCode) {
    // Validate language against supported languages before committing
    import('aldoo-ra/Localization/api').then(async ({ GetSupportedLanguages }) => {
      try {
        const supported = await GetSupportedLanguages()
        const isValid = Array.isArray(supported) && supported.some(lang => lang.code === langCode)

        if (!isValid) {
          // Restore the previous language
          const prev = window.__prevLangBeforeUrlSwitch ?? null
          if (prev === null) {
            localStorage.removeItem('current-site-language')
            document.cookie = `preferred-language=; path=/; max-age=0; SameSite=Lax`
          } else {
            localStorage.setItem('current-site-language', prev)
            try {
              const prevCode = JSON.parse(prev)
              document.cookie = `preferred-language=${prevCode}; path=/; max-age=31536000; SameSite=Lax`
            } catch (e) {}
          }
          // Redirect to home without the invalid language prefix
          window.location.replace('/')
          return
        }

        // Valid language — commit to localStorage and cookie
        if (typeof localStorage !== 'undefined') {
          const currentLang = localStorage.getItem('current-site-language')
          if (currentLang !== `"${langCode}"`) {
            localStorage.setItem('current-site-language', JSON.stringify(langCode))
          }
        }
        if (typeof document !== 'undefined') {
          document.cookie = `preferred-language=${langCode}; path=/; max-age=31536000; SameSite=Lax`
        }

        // Call setLanguageGlobal for key refreshes and UI updates
        const { setLanguageGlobal } = await import('aldoo-ra/Localization')
        setLanguageGlobal(langCode)
      } catch (err) {
        console.warn('[RouteReader] Language validation failed:', err)
      }
    }).catch(err => {
      console.warn('[RouteReader] Failed to import Localization API:', err)
    })
  }

  static clearCache() {
    this.#routeCache.clear()
    this.initialized = false
    this.#initializing = false
  }
}